import { useEffect, useState, useCallback } from "react"
import { supabase } from "@/lib/supabase"
import { useToast } from "@/context/ToastContext"
import { sendGracePeriodReminder, takeoverSlot } from "@/services/user.service"
import { Badge } from "@/components/common/Badge"
import { Button } from "@/components/common/Button"
import { EmptyState } from "@/components/common/EmptyState"
import { LoadingRow } from "@/components/common/Spinner"
import { Mail, Building2, RefreshCw, AlertTriangle } from "lucide-react"

interface GraceUser {
  id: string
  email: string
  full_name: string | null
  network_size: number
  subscriptions: { product_slug: string; grace_period_end: string | null }[]
}

function daysLeft(dateStr: string | null): number {
  if (!dateStr) return 0
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000)
}

export function GracePeriodsPage() {
  const { toast } = useToast()
  const [users, setUsers]   = useState<GraceUser[]>([])
  const [loading, setLoading] = useState(true)
  const [acting, setActing]   = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from("users")
        .select("id, email, full_name, network_size, subscriptions(product_slug, grace_period_end)")
        .eq("subscription_status", "grace_period")
        .order("created_at", { ascending: false })
      if (error) throw error
      setUsers((data ?? []) as GraceUser[])
    } catch { toast("Failed to load grace periods", "error") }
    finally { setLoading(false) }
  }, [toast])

  useEffect(() => { load() }, [load])

  const expiring7  = users.filter(u => { const d = daysLeft(u.subscriptions?.[0]?.grace_period_end ?? null); return d <= 7 && d > 0 }).length
  const expiringToday = users.filter(u => daysLeft(u.subscriptions?.[0]?.grace_period_end ?? null) <= 0).length

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total in Grace",    value: users.length, color: "border-t-amber-500" },
          { label: "Expiring in 7 Days", value: expiring7,   color: "border-t-orange-500" },
          { label: "Expired Today",      value: expiringToday, color: "border-t-red-500" },
        ].map(s => (
          <div key={s.label} className={`bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden`}>
            <div className={`h-1 ${s.color.replace("border-t-", "bg-")}`} />
            <div className="p-5">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">{s.label}</p>
              <p className="text-3xl font-black text-gray-900">{loading ? "—" : s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex justify-between items-center">
        <div />
        <div className="flex gap-2">
          <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={load}>Refresh</Button>
          <Button variant="secondary" icon={<Mail size={14} />} onClick={() => toast("Bulk reminders — Phase 4", "info")}>
            Send All Reminders
          </Button>
          <Button variant="danger" icon={<AlertTriangle size={14} />} onClick={() => toast("Process expired — Phase 4", "info")}>
            Process Expired
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                {["User", "Products", "Grace Ends", "Days Left", "Downline Value", "Actions"].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <LoadingRow cols={6} /> :
               users.length === 0 ? (
                <tr><td colSpan={6}>
                  <EmptyState icon="✅" title="No users in grace period" subtitle="All members are current on their payments." />
                </td></tr>
               ) : users.map(u => {
                const end = u.subscriptions?.[0]?.grace_period_end ?? null
                const days = daysLeft(end)
                const emojiMap: Record<string, string> = { zimrah: "🌸", hpc: "📅", almanac: "🌾" }
                return (
                  <tr key={u.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-bold text-sm text-gray-900">{u.full_name ?? "—"}</p>
                      <p className="text-xs text-gray-400">{u.email}</p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-1">
                        {u.subscriptions?.map(s => (
                          <span key={s.product_slug} className="text-lg">{emojiMap[s.product_slug] ?? "📱"}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {end ? new Date(end).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={days <= 0 ? "danger" : days <= 3 ? "warning" : "info"} dot>
                        {days <= 0 ? "Expired" : `${days} days`}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 font-bold text-gray-900">
                      ${((u.subscriptions?.length ?? 0) * 6.99).toFixed(2)}/mo
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <Button size="sm" variant="secondary" icon={<Mail size={13} />}
                          loading={acting === u.id + "remind"}
                          onClick={async () => {
                            setActing(u.id + "remind")
                            try { await sendGracePeriodReminder(u.id, u.email); toast("Reminder sent", "success") }
                            catch { toast("Failed to send reminder", "error") }
                            finally { setActing(null) }
                          }}>
                          Remind
                        </Button>
                        <Button size="sm" variant="danger" icon={<Building2 size={13} />}
                          loading={acting === u.id + "takeover"}
                          onClick={async () => {
                            setActing(u.id + "takeover")
                            try { await takeoverSlot(u.id, "non_payment"); toast("Slot taken over", "success"); load() }
                            catch { toast("Failed to take over slot", "error") }
                            finally { setActing(null) }
                          }}>
                          Takeover
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}